import { ShowBase, useResourceContext, useShowContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { MobileBackButton } from "../misc/MobileBackButton";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { OutreachList } from "../types";
import { AddContactToListDialog } from "./AddContactToListDialog";
import { OutreachListMembersTable } from "./OutreachListMembersTable";

export const OutreachListShow = () => {
  const isMobile = useIsMobile();
  return (
    <ShowBase>
      {isMobile ? <OutreachListShowContentMobile /> : <OutreachListShowContent />}
    </ShowBase>
  );
};

const OutreachListShowContent = () => {
  const translate = useTranslate();
  const resource = useResourceContext();
  const { record, isPending } = useShowContext<OutreachList>();
  if (isPending || !record) return null;
  const isChannel = record.criteria?.list_kind === "channel";

  return (
    <div className="mt-2 mb-2">
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold">{record.name}</h1>
                {isChannel && <Badge variant="secondary">{record.identity}</Badge>}
                {!record.is_active && <Badge variant="outline">Inactive</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {record.purpose}
              </p>
            </div>
            <AddContactToListDialog list={record} />
          </div>
          <OutreachListMembersTable list={record} />
        </CardContent>
      </Card>
      <Link
        to={`/${resource}`}
        className="inline-block mt-4 text-sm text-muted-foreground hover:underline"
      >
        {translate("ra.action.back", { _: "Back" })}
      </Link>
    </div>
  );
};

const OutreachListShowContentMobile = () => {
  const resource = useResourceContext();
  const { record, isPending } = useShowContext<OutreachList>();
  if (isPending || !record) return null;
  const isChannel = record.criteria?.list_kind === "channel";

  return (
    <>
      <MobileHeader>
        <MobileBackButton to={`/${resource}`} />
        <div className="flex flex-1 min-w-0">
          <h1 className="truncate text-xl font-semibold">{record.name}</h1>
        </div>
      </MobileHeader>
      <MobileContent>
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {isChannel && <Badge variant="secondary">{record.identity}</Badge>}
            {!record.is_active && <Badge variant="outline">Inactive</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {record.purpose}
          </p>
        </div>
        <div className="mb-4">
          <AddContactToListDialog list={record} />
        </div>
        <OutreachListMembersTable list={record} />
      </MobileContent>
    </>
  );
};
